import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormGroup, Validators, ReactiveFormsModule, FormControl } from '@angular/forms';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { AlertService } from '../../alert.service';
import { SmsCallService } from '../sms-call.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { BusinessInformation } from '../sms-call';

@Component({
  selector: 'app-a2p-registration-business-information',
  templateUrl: './a2p-registration-business-information.component.html',
  styleUrls: ['./a2p-registration-business-information.component.css'],
  imports: [ReactiveFormsModule, NgbPopover, FaIconComponent]
})
export class A2PRegistrationBusinessInformation implements OnInit {

  // Properties
  @Output() proceedToNextStep: EventEmitter<void> = new EventEmitter<void>();
  @Input() businessInformation: BusinessInformation;

  businessIdentityForm: FormGroup;

  businessTypeValues: { name: string, value: string }[] = [
    {
      "name": "Co-Operative",
      "value": "Co-operative"
    },
    {
      "name": "Corporation",
      "value": "Corporation"
    },
    {
      "name": "Limited Liability Corporation",
      "value": "Limited Liability Corporation"
    },
    {
      "name": "Non-Profit Corporation",
      "value": "Non-profit Corporation"
    },
    {
      "name": "Partnership",
      "value": "Partnership"
    }
  ];

  businessIndustryValues: { name: string, value: string }[] = [
    {
      "name": "Agriculture",
      "value": "AGRICULTURE"
    },
    {
      "name": "Automotive",
      "value": "AUTOMOTIVE"
    },
    {
      "name": "Banking",
      "value": "BANKING"
    },
    {
      "name": "Construction",
      "value": "CONSTRUCTION"
    },
    {
      "name": "Consumer",
      "value": "CONSUMER"
    },
    {
      "name": "Education",
      "value": "EDUCATION"
    },
    {
      "name": "Electronics",
      "value": "ELECTRONICS"
    },
    {
      "name": "Engineering",
      "value": "ENGINEERING"
    },
    {
      "name": "Energy",
      "value": "ENERGY"
    },
    {
      "name": "Fast Moving Consumer Goods",
      "value": "FAST_MOVING_CONSUMER_GOODS"
    },
    {
      "name": "Financial",
      "value": "FINANCIAL"
    },
    {
      "name": "Fintech",
      "value": "FINTECH"
    },
    {
      "name": "Food and Beverage",
      "value": "FOOD_AND_BEVERAGE"
    },
    {
      "name": "Government",
      "value": "GOVERNMENT"
    },
    {
      "name": "Healthcare",
      "value": "HEALTHCARE"
    },
    {
      "name": "Hospitality",
      "value": "HOSPITALITY"
    },
    {
      "name": "Insurance",
      "value": "INSURANCE"
    },
    {
      "name": "Jewelery",
      "value": "JEWELRY"
    },
    {
      "name": "Legal",
      "value": "LEGAL"
    },
    {
      "name": "Manufacturing",
      "value": "MANUFACTURING"
    },
    {
      "name": "Media",
      "value": "MEDIA"
    },
    {
      "name": "Not For Profit",
      "value": "NOT_FOR_PROFIT"
    },
    {
      "name": "Oil and Gas",
      "value": "OIL_AND_GAS"
    },
    {
      "name": "Online",
      "value": "ONLINE"
    },
    {
      "name": "Professional Services",
      "value": "PROFESSIONAL_SERVICES"
    },
    {
      "name": "Raw Materials",
      "value": "RAW_MATERIALS"
    },
    {
      "name": "Real Estate",
      "value": "REAL_ESTATE"
    },
    {
      "name": "Religion",
      "value": "RELIGION"
    },
    {
      "name": "Retail",
      "value": "RETAIL"
    },
    {
      "name": "Technology",
      "value": "TECHNOLOGY"
    },
    {
      "name": "Telecommunications",
      "value": "TELECOMMUNICATIONS"
    },
    {
      "name": "Transporation",
      "value": "TRANSPORTATION"
    },
    {
      "name": "Travel",
      "value": "TRAVEL"
    }
  ];

  businessRegistrationIdentifierValues: { name: string, value: string }[] = [
    {
      "name": "EIN",
      "value": "EIN"
    },
    {
      "name": "DUNS",
      "value": "DUNS"
    },
    {
      "name": "CBN",
      "value": "CBN"
    },
    {
      "name": "CN",
      "value": "CN"
    },
    {
      "name": "ACN",
      "value": "ACN"
    },
    {
      "name": "CIN",
      "value": "CIN"
    },
    {
      "name": "VAT",
      "value": "VAT"
    },
    {
      "name": "VATRN",
      "value": "VATRN"
    },
    {
      "name": "RN",
      "value": "RN"
    },
    {
      "name": "OTHER",
      "value": "Other"
    }
  ];

  // Font Awesome Properties
  faQuestionCircle = faQuestionCircle;

  // Injections
  private smsCallService = inject(SmsCallService);
  private alertService = inject(AlertService);

  ngOnInit(): void {
    this.businessIdentityForm = new FormGroup({
      businessIndustry: new FormControl('SELECT', Validators.required),
      businessName: new FormControl(null, Validators.required),
      businessRegistrationIdentifier: new FormControl('SELECT', Validators.required),
      businessRegistrationNumber: new FormControl(null, Validators.required),
      businessType: new FormControl('SELECT', Validators.required),
      socialMediaProfileUrls: new FormControl(),
      websiteUrl: new FormControl()
    });
    if (this.businessInformation) this.setBusinessForm();
  }

  // Set Business Form
  private setBusinessForm(): void {
    const formValues = this.businessInformation;
    delete formValues.sid;
    this.businessIdentityForm.setValue({ ...formValues });
  }

  // Submit Form
  submitForm(): void {
    if (this.businessIdentityForm.valid) {
      const payload = { ...this.businessIdentityForm.value };
      if (this.businessInformation) {
        this.smsCallService.updateBusinessRegistration({
          ...payload
        }).subscribe(() => {
          this.alertService.showSuccessAlert('Business Information Updated');
          this.proceedToNextStep.emit();
        });
      } else {
        this.smsCallService.createBusinessRegistration(payload).subscribe(() => {
          this.alertService.showSuccessAlert('Business Information Saved');
          this.proceedToNextStep.emit();
        });
      }
    } else {
      this.businessIdentityForm.markAllAsTouched();
    }
  }

  // Form Accessors
  get businessIndustry() { return this.businessIdentityForm.controls.businessIndustry; }
  get businessName() { return this.businessIdentityForm.controls.businessName; }
  get businessRegistrationIdentifier() { return this.businessIdentityForm.controls.businessRegistrationIdentifier; }
  get businessRegistrationNumber() { return this.businessIdentityForm.controls.businessRegistrationNumber; }
  get businessType() { return this.businessIdentityForm.controls.businessType; }
}
